import React from 'react';
import PropTypes from 'prop-types';
import { getCardImageSrc, handleCardImageError } from '../hooks/useCardImage';
import { getCardRarityClass } from '../utils/cardUtils';

const PerkDetails = ({ perkDetails }) => {
    if (!perkDetails || perkDetails.length === 0) return null;

    const superStars = perkDetails.filter(p => p.perk === 'superStar');
    const drugDealers = perkDetails.filter(p => p.perk === 'drugDealer');
    const drugDealerHits = drugDealers.filter(p => p.result.includes('+1'));

    return (
        <div className="postgame-perk-details">
            {superStars.length > 0 && (
                <div className="postgame-perk-section">
                    <div className="postgame-perk-header">Super Star</div>
                    {superStars.map((p, i) => (
                        <div key={`star-${i}`} className="postgame-perk-row">
                            <span className="postgame-perk-flavor">{p.result}</span>
                        </div>
                    ))}
                </div>
            )}
            {drugDealers.length > 0 && (
                <div className="postgame-perk-section">
                    <div className="postgame-perk-header">
                        Drug Dealer {drugDealerHits.length > 0 ? `(${drugDealerHits.length} scored)` : '(none scored)'}
                    </div>
                    {drugDealers.map((p, i) => (
                        <div key={`dealer-${i}`} className={`postgame-perk-row ${p.result.includes('+1') ? '' : 'postgame-perk-miss'}`}>
                            <span className="postgame-perk-flavor">{p.result}</span>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const RewardItems = ({ reward, hidePerkDetails = false }) => {
    if (!reward) return null;
    const { pack, card, money, energyDrinks, energyDrinkCapped, perkDetails } = reward;
    const hasAnything = pack || card || money || energyDrinks || energyDrinkCapped || (!hidePerkDetails && perkDetails && perkDetails.length > 0);
    if (!hasAnything) return null;

    return (
        <div className="postgame-reward-items">
            {pack && (
                <div className="postgame-reward-item">
                    <div className="postgame-reward-img-wrapper">
                        <img
                            src={getCardImageSrc(pack.seriesKey)}
                            alt={pack.seriesName}
                            className="postgame-reward-pack-img"
                            onError={handleCardImageError}
                        />
                        {pack.numberOfSeries > 1 && (
                            <span className="postgame-reward-multiplier">x{pack.numberOfSeries}</span>
                        )}
                    </div>
                    <div className="postgame-reward-label">{pack.seriesName}</div>
                </div>
            )}
            {card && (
                <div className="postgame-reward-item">
                    <div className="postgame-reward-img-wrapper postgame-reward-card-wrapper">
                        <img
                            src={getCardImageSrc(card.cardSeriesKey)}
                            alt={card.name}
                            className="postgame-reward-card-img"
                            onError={handleCardImageError}
                        />
                        <span className={`postgame-reward-card-rating ${getCardRarityClass(card.rating)}`}>
                            {card.rating}
                        </span>
                    </div>
                    <div className="postgame-reward-label">
                        {card.variationName && <span className="postgame-reward-variation">{card.variationName} </span>}
                        {card.name}
                    </div>
                </div>
            )}
            {money != null && money > 0 && (
                <div className="postgame-reward-item">
                    <div className="postgame-reward-money">${Number(money).toLocaleString()}</div>
                    <div className="postgame-reward-label">Cash</div>
                </div>
            )}
            {energyDrinks != null && energyDrinks > 0 && (
                <div className="postgame-reward-item">
                    <div className="postgame-reward-energy-drink-icon">
                        ⚡
                        <span className="postgame-reward-multiplier">x{energyDrinks}</span>
                    </div>
                    <div className="postgame-reward-label">
                        Energy Drink{energyDrinks > 1 ? 's' : ''}
                    </div>
                </div>
            )}
            {energyDrinkCapped && (
                <div className="postgame-reward-item postgame-reward-capped">
                    <div className="postgame-reward-energy-drink-icon">⚡</div>
                    <div className="postgame-reward-label">Energy drinks maxed out — use some to earn more!</div>
                </div>
            )}
            {!hidePerkDetails && <PerkDetails perkDetails={perkDetails} />}
        </div>
    );
};

RewardItems.propTypes = {
    reward: PropTypes.shape({
        pack: PropTypes.object,
        card: PropTypes.object,
        money: PropTypes.number,
        energyDrinks: PropTypes.number,
        perkDetails: PropTypes.arrayOf(PropTypes.shape({
            playerName: PropTypes.string,
            perk: PropTypes.string,
            points: PropTypes.number,
            result: PropTypes.string,
        })),
    }),
};

export default RewardItems;
